import React, { useState, useEffect, useMemo, useCallback, useRef, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  TableRow,
  TableCell,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  Autocomplete,
  Card,
  CardContent,
  Pagination,
  Stack,
  FormControl,
  InputLabel,
  Select,
  Grid,
} from '@mui/material';
import DataTable from '../components/DataTable';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import backendApi from '../shared/api/backendApi';

const NEW_USER_PURCHASE_MESSAGE = 'You have no purchase data yet. Add your first purchase to get started.';

const isExpectedEmptyDataScenario = (error) => {
  const status = error?.response?.status;
  const errorText = `${error?.response?.data?.error || ''} ${error?.response?.data?.message || ''}`.toLowerCase();

  if (status !== 400) {
    return false;
  }

  return (
    errorText.includes('company not found') ||
    errorText.includes('business owner authentication required') ||
    errorText.includes('no purchase data yet') ||
    errorText.includes('no inventory data yet')
  );
};

const logDuration = (label, startedAt) => {
  const duration = performance.now() - startedAt;
  console.log(`${label}: ${duration.toFixed(2)} ms`);
};

const transformPurchaseRecord = (purchase = {}) => ({
  id: purchase.id,
  'Purchase ID': purchase.id,
  'Date': purchase['Date'] || purchase.invoice_date,
  'Dealer Name': purchase['Dealer Name'] || purchase.dealer_name,
  'Item Name': purchase['Item Name'] || purchase.item_name,
  'Quantity': purchase['Quantity'] || purchase.quantity,
  'Unit': purchase['Unit'] || purchase.unit,
  'Purchase Price/Unit': purchase['Purchase Price'] || purchase.purchase_price,
  'Selling Price/Unit': purchase['Selling Price'] || purchase.selling_price,
  'Amount': purchase['Amount'] || purchase.amount,
  'Notes': purchase['Notes'] || purchase.notes,
  'Status': purchase['Status'] || purchase.status,
  'Invoice No': purchase['Invoice No'] || purchase.invoice_no,
  ...purchase,
});

// Memoized component for total purchase value
const TotalPurchaseValue = React.memo(({ totalValue, totalCount, searchTerm, filteredCount }) => {
  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
        ₹{totalValue.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
        Total of all purchases • {totalCount} total purchases{searchTerm ? ` (${filteredCount} filtered)` : ''}
      </Typography>
    </Box>
  );
});
TotalPurchaseValue.displayName = 'TotalPurchaseValue';

// Memoized component for formatted amount display
const FormattedAmount = React.memo(({ amount, variant = 'body2', sx = {} }) => {
  const formatted = useMemo(() => {
    const num = typeof amount === 'string' ? parseFloat(amount.replace(/,/g, '')) : parseFloat(amount);
    return `₹${(isNaN(num) ? 0 : num).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  }, [amount]);

  return (
    <Typography variant={variant} sx={sx}>
      {formatted}
    </Typography>
  );
});
FormattedAmount.displayName = 'FormattedAmount';

// Purchases table columns configuration
const PURCHASES_COLUMNS = [
  { key: 'date', label: 'Date', width: '9%' },
  { key: 'dealer', label: 'Dealer Name', width: '14%' },
  { key: 'item', label: 'Item Name', width: '17%' },
  { key: 'quantity', label: 'Quantity', width: '6%', align: 'right' },
  { key: 'unit', label: 'Unit', width: '6%', align: 'center' },
  { key: 'purchasePrice', label: 'Purchase Price', width: '12%' },
  { key: 'sellingPrice', label: 'Selling Price', width: '12%' },
  { key: 'amount', label: 'Amount', width: '11%' },
  { key: 'actions', label: 'Actions', width: '5%' },
];

// Custom row renderer for purchases table
const renderPurchaseRow = (purchase, index, onEdit, onDelete, navigate) => {
  const amount = (() => {
    const amt = purchase['Amount'] || 0;
    const num = typeof amt === 'string' ? parseFloat(amt.replace(/,/g, '')) : parseFloat(amt);
    return isNaN(num) ? 0 : num;
  })();

  return (
    <TableRow
      key={purchase.id || `purchase-${index}`}
      sx={{
        backgroundColor: index % 2 === 0 ? 'white' : '#f8fafc',
        '&:hover': { backgroundColor: '#e2e8f0' },
        minHeight: 60,
        '& .MuiTableCell-root': {
          padding: '8px 6px',
          overflow: 'visible',
          whiteSpace: 'normal'
        }
      }}
    >
      <TableCell sx={{ pr: 0.5 }}>
        <Box
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            px: 0.5,
            py: 0.5,
            borderRadius: 1.5,
            display: 'inline-block',
            fontSize: '0.7rem',
            fontWeight: 700,
            letterSpacing: '0.3px',
            textAlign: 'center',
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {purchase['Date']}
        </Box>
      </TableCell>
      <TableCell sx={{ pl: 0.5 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            fontSize: '0.85rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {purchase['Dealer Name'] || purchase.dealer_name || purchase.name || 'N/A'}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            fontSize: '0.8rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {purchase['Item Name']}
        </Typography>
      </TableCell>
      <TableCell sx={{ textAlign: 'right' }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {purchase['Quantity']}
        </Typography>
      </TableCell>
      <TableCell sx={{ textAlign: 'center' }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {purchase['Unit']}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
          <FormattedAmount amount={purchase['Purchase Price/Unit']} />
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
          <FormattedAmount amount={purchase['Selling Price/Unit']} />
        </Typography>
      </TableCell>
      <TableCell
        sx={{
          overflow: 'visible',
          whiteSpace: 'normal',
          verticalAlign: 'top',
          padding: '8px 6px !important',
          '& .MuiBox-root': {
            width: '100%',
            overflow: 'visible'
          }
        }}
      >
        <Box sx={{ width: '100%', overflow: 'visible' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', fontWeight: 600, display: 'block', mb: 0.3 }}>
            TOTAL AMOUNT
          </Typography>
          <FormattedAmount
            amount={amount}
            variant="body2"
            sx={{ fontWeight: 700, color: 'primary.main', fontSize: '0.9rem', display: 'block' }}
          />
        </Box>
      </TableCell>
      <TableCell sx={{ padding: '8px 4px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <IconButton
            onClick={() => onEdit(purchase)}
            size="small"
            color="primary"
            title="Edit Purchase"
            sx={{ padding: '4px' }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            onClick={() => onDelete(purchase)}
            size="small"
            color="error"
            title="Delete Purchase"
            sx={{ padding: '4px' }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </TableCell>
    </TableRow>
  );
};

function Purchases() {
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [infoMessage, setInfoMessage] = useState(null);
  const [success, setSuccess] = useState(null);
  const [open, setOpen] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [deletingPurchase, setDeletingPurchase] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    date: new Date().toISOString().slice(0,10),
    dealerName: '',
    itemName: '',
    quantity: '',
    unit: 'PCS',
    purchasePrice: '',
    sellingPrice: '',
    notes: ''
  });

  // Pagination state
  const [allPurchases, setAllPurchases] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalPurchasesCount, setTotalPurchasesCount] = useState(0);
  const [totalPurchasesValue, setTotalPurchasesValue] = useState(0);
  const [pageSize, setPageSize] = useState(50); // Keep for backward compatibility

  // Performance optimization states
  const [fullDataLoaded, setFullDataLoaded] = useState(false);
  const [loadingFullData, setLoadingFullData] = useState(false);
  const [fullPurchasesData, setFullPurchasesData] = useState(null);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const lastSearchQueryRef = useRef('');

  const invalidatePurchasesCache = useCallback(() => {
    localStorage.removeItem('purchases_full_data_cache');
    localStorage.removeItem('purchases_full_data_timestamp');
    localStorage.removeItem('purchases_dealers_cache');
    localStorage.removeItem('purchases_inventory_cache');
    localStorage.removeItem('purchases_static_data_timestamp');
    localStorage.removeItem('sales_inventory_cache');
    localStorage.removeItem('sales_customers_cache');
    localStorage.removeItem('sales_static_data_timestamp');
    localStorage.removeItem('sales_full_data_cache');
    localStorage.removeItem('sales_full_data_timestamp');
    setFullDataLoaded(false);
    setFullPurchasesData(null);
  }, []);

  useEffect(() => {
    loadStaticData();
    loadInitialPurchasesData();
    loadTotalPurchasesValue();
  }, []);

  // Debounce search term to reduce filter calculations
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Handle search term changes and trigger full data loading if needed
  useEffect(() => {
    const normalizedQuery = debouncedSearchTerm.trim().toLowerCase();
    setCurrentPage(1);

    if (!normalizedQuery) {
      lastSearchQueryRef.current = '';
      return;
    }

    const isNewQuery = lastSearchQueryRef.current !== normalizedQuery;
    lastSearchQueryRef.current = normalizedQuery;

    // Only trigger full-data loading for a new search query.
    if (isNewQuery && !fullDataLoaded && !loadingFullData) {
      console.log('🔍 Search initiated - loading full dataset for comprehensive search');
      loadFullPurchasesData();
    }
  }, [debouncedSearchTerm, fullDataLoaded, loadingFullData]);

  // Load static data (dealers and inventory) from backend (no local cache)
  const loadStaticData = useCallback(async () => {
    const startedAt = performance.now();
    try {
      try {
        const dealersData = await backendApi.getDealers();
        setDealers(dealersData);
        console.log(`Loaded ${dealersData.length} dealers from backend`);
      } catch (err) {
        if (isExpectedEmptyDataScenario(err)) {
          setDealers([]);
        } else {
          console.error('Error loading dealers:', err);
        }
      }

      const inventoryStartedAt = performance.now();
      setInventoryLoading(true);
      try {
        const inventoryResponse = await backendApi.getInventory();
        const inventoryData = inventoryResponse.inventory || [];
        setInventory(inventoryData);
        console.log(`Loaded ${inventoryData.length} inventory items from backend`);
      } catch (err) {
        if (isExpectedEmptyDataScenario(err)) {
          setInventory([]);
        } else {
          console.error('Error loading inventory:', err);
        }
      } finally {
        setInventoryLoading(false);
        logDuration('loadInventory', inventoryStartedAt);
      }
    } catch (err) {
      console.error('Error in loadStaticData:', err);
    } finally {
      logDuration('loadStaticData', startedAt);
    }
  }, []);

  // Load initial purchases data (first page only for fast loading)
  const loadInitialPurchasesData = useCallback(async () => {
    const startedAt = performance.now();
    try {
      setLoading(true);
      setError(null);

      const purchasesResponse = await backendApi.getPurchases(1, 50);
      const purchasesData = purchasesResponse.purchases || [];
      const totalPurchases = purchasesResponse.totalPurchases || 0;

      const transformedPurchases = purchasesData.map(transformPurchaseRecord);

      setAllPurchases(transformedPurchases);
      setCurrentPage(1);
      setTotalPurchasesCount(totalPurchases);
      setFullDataLoaded(false);

      if (totalPurchases === 0) {
        setInfoMessage(NEW_USER_PURCHASE_MESSAGE);
      } else {
        setInfoMessage(null);
      }

      console.log(`Loaded initial purchases data: ${transformedPurchases.length} purchases (total: ${totalPurchases})`);
    } catch (err) {
      if (isExpectedEmptyDataScenario(err)) {
        setAllPurchases([]);
        setCurrentPage(1);
        setTotalPurchasesCount(0);
        setError(null);
        setInfoMessage(NEW_USER_PURCHASE_MESSAGE);
      } else {
        setInfoMessage(null);
        setError('Failed to load purchases data. Please try again.');
        console.error('Error loading purchases data:', err);
      }
    } finally {
      setLoading(false);
      logDuration('loadInitialPurchasesData', startedAt);
    }
  }, []);

  // Load full purchases data for search functionality
  const loadFullPurchasesData = useCallback(async () => {
    const startedAt = performance.now();
    try {
      setLoadingFullData(true);

      const purchasesResponse = await backendApi.getPurchases(1, 5000);
      const purchasesData = purchasesResponse.purchases || [];
      const totalPurchases = purchasesResponse.totalPurchases || 0;

      const transformedPurchases = purchasesData.map(transformPurchaseRecord);

      setFullPurchasesData(transformedPurchases);
      setFullDataLoaded(true);
      setTotalPurchasesCount(totalPurchases);

      if (totalPurchases === 0) {
        setInfoMessage(NEW_USER_PURCHASE_MESSAGE);
      }
    } catch (err) {
      if (isExpectedEmptyDataScenario(err)) {
        setAllPurchases([]);
        setTotalPurchasesCount(0);
        setError(null);
        setInfoMessage(NEW_USER_PURCHASE_MESSAGE);
      } else {
        console.error('Error loading full purchases data:', err);
        setError('Failed to load full purchases data for search. Using initial data.');
      }
    } finally {
      setLoadingFullData(false);
      logDuration('loadFullPurchasesData', startedAt);
    }
  }, []);

  // Load total purchases value from analytics API
  const loadTotalPurchasesValue = useCallback(async () => {
    try {
      const analyticsResponse = await backendApi.getDashboardMetrics();
      const totalPurchases = analyticsResponse.metrics?.totalPurchases || 0;
      setTotalPurchasesValue(totalPurchases);
    } catch (err) {
      if (isExpectedEmptyDataScenario(err)) {
        setTotalPurchasesValue(0);
      } else {
        console.error('Error loading total purchases value:', err);
      }
    }
  }, []);

  // Handle page changes - load data from backend when not searching
  const handlePageChange = useCallback(async (event, newPage) => {
    console.log(`📄 Page change requested: ${currentPage} -> ${newPage}`);

    // If searching, pagination is handled client-side on already loaded data
    if (debouncedSearchTerm.trim()) {
      setCurrentPage(newPage);
      return;
    }

    // For non-search pagination, load data from backend
    try {
      setLoading(true);
      setError(null);

      const purchasesResponse = await backendApi.getPurchases(newPage, rowsPerPage);

      const purchasesData = purchasesResponse.purchases || [];
      const totalPurchases = purchasesResponse.totalPurchases || 0;

      // Transform purchases data
      const transformedPurchases = purchasesData.map(transformPurchaseRecord);

      console.log(`📊 Loaded page ${newPage} purchases data: ${transformedPurchases.length} purchases (total: ${totalPurchases})`);

      setAllPurchases(transformedPurchases);
      setCurrentPage(newPage);
      setTotalPurchasesCount(totalPurchases);
    } catch (err) {
      if (isExpectedEmptyDataScenario(err)) {
        setAllPurchases([]);
        setTotalPurchasesCount(0);
        setCurrentPage(1);
        setError(null);
        setInfoMessage(NEW_USER_PURCHASE_MESSAGE);
      } else {
        setError('Failed to load purchases data for the selected page. Please try again.');
        console.error('Error loading page data:', err);
      }
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearchTerm, rowsPerPage]);

  const handleOpenDialog = useCallback((purchase = null) => {
    if (purchase) {
      setEditingPurchase(purchase);
      // Format date properly for HTML date input (yyyy-MM-dd)
      let formattedDate = new Date().toISOString().slice(0,10);
      if (purchase['Date']) {
        try {
          // Handle different date formats from Google Sheets
          const dateStr = purchase['Date'];
          let formattedDateStr = '';

          if (dateStr.includes('/')) {
            // Handle MM/dd/yyyy format
            const [month, day, year] = dateStr.split('/');
            // Pad month and day with leading zeros
            const paddedMonth = month.padStart(2, '0');
            const paddedDay = day.padStart(2, '0');
            formattedDateStr = `${year}-${paddedMonth}-${paddedDay}`;
          } else if (dateStr.includes('-')) {
            // Handle yyyy-MM-dd or dd-MM-yyyy format
            const parts = dateStr.split('-');
            if (parts.length === 3) {
              if (parts[0].length === 4) {
                // Already yyyy-MM-dd
                formattedDateStr = dateStr;
              } else {
                // Assume dd-MM-yyyy, convert to yyyy-MM-dd
                const [day, month, year] = parts;
                const paddedMonth = month.padStart(2, '0');
                const paddedDay = day.padStart(2, '0');
                formattedDateStr = `${year}-${paddedMonth}-${paddedDay}`;
              }
            }
          }

          if (formattedDateStr) {
            formattedDate = formattedDateStr;
          }
        } catch (error) {
          console.warn('Error parsing date:', purchase['Date'], error);
        }
      }

      // Extract item data from the purchase (database structure)
      const item = purchase.items && purchase.items.length > 0 ? purchase.items[0] : {};

      setFormData({
        date: formattedDate,
        dealerName: purchase['Dealer Name'] || purchase.dealer_name || '',
        itemName: purchase['Item Name'] || item.item_name || '',
        quantity: parseFloat(purchase['Quantity'] || item.quantity || 0),
        unit: purchase['Unit'] || item.unit || 'PCS',
        purchasePrice: parseFloat(purchase['Purchase Price'] || item.unit_price || 0),
        sellingPrice: parseFloat(purchase['Selling Price'] || item.selling_price || 0),
        notes: purchase['Notes'] || purchase.notes || ''
      });
    } else {
      setEditingPurchase(null);
      setFormData({
        date: new Date().toISOString().slice(0,10),
        dealerName: '',
        itemName: '',
        quantity: '',
        unit: 'PCS',
        purchasePrice: '',
        sellingPrice: '',
        notes: ''
      });
    }
    setOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setOpen(false);
    setEditingPurchase(null);
    setFormData({
      date: new Date().toISOString().slice(0,10),
      dealerName: '',
      itemName: '',
      quantity: '',
      unit: 'PCS',
      purchasePrice: '',
      sellingPrice: '',
      notes: ''
    });
  }, []);

  const handleSubmit = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      setInfoMessage(null);

      // Validate form
      if (!formData.dealerName || !formData.itemName || formData.quantity <= 0) {
        setError('Please fill in all required fields with valid values.');
        return;
      }

      if (editingPurchase) {
        // Update existing purchase
        console.log('🔄 Updating existing purchase');

        // Use backend API to update purchase
        const purchaseData = {
          date: formData.date,
          dealerName: formData.dealerName,
          itemName: formData.itemName,
          quantity: formData.quantity,
          unit: formData.unit,
          purchasePrice: formData.purchasePrice,
          sellingPrice: formData.sellingPrice,
          notes: formData.notes || ''
        };

        await backendApi.updatePurchase(editingPurchase.id, purchaseData);

        setSuccess('Purchase updated successfully!');
      } else {
        // Add new purchase
        console.log('➕ Adding new purchase');

        const purchaseData = {
          date: formData.date,
          dealerName: formData.dealerName,
          itemName: formData.itemName,
          quantity: formData.quantity,
          unit: formData.unit,
          purchasePrice: formData.purchasePrice,
          sellingPrice: formData.sellingPrice,
          notes: formData.notes || ''
        };

        await backendApi.addPurchase(purchaseData);

        setSuccess('Purchase added successfully!');
      }

      invalidatePurchasesCache();
      await loadInitialPurchasesData();
      await loadTotalPurchasesValue();
      handleCloseDialog();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to save purchase. Please try again.');
      console.error('Error saving purchase:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeleteDialog = useCallback((purchase) => {
    setDeletingPurchase(purchase);
    setDeleteDialogOpen(true);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
    setDeletingPurchase(null);
  }, []);

  const handleConfirmDelete = async () => {
    if (!deletingPurchase?.id) return;

    try {
      setDeleting(true);
      setError(null);
      setSuccess(null);
      setInfoMessage(null);

      await backendApi.deletePurchase(deletingPurchase.id);

      invalidatePurchasesCache();
      await loadInitialPurchasesData();
      await loadTotalPurchasesValue();
      setSuccess('Purchase deleted successfully!');
      handleCloseDeleteDialog();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      const backendMessage = err?.response?.data?.message;
      setError(backendMessage || 'Failed to delete purchase. Please try again.');
      console.error('Error deleting purchase:', err);
    } finally {
      setDeleting(false);
    }
  };

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  // Pre-compute searchable data structures for better performance
  const searchSourcePurchases = useMemo(() => {
    if (fullDataLoaded && Array.isArray(fullPurchasesData)) {
      return fullPurchasesData;
    }
    return allPurchases;
  }, [allPurchases, fullDataLoaded, fullPurchasesData]);

  const searchablePurchases = useMemo(() => {
    if (!debouncedSearchTerm.trim()) {
      return [];
    }

    return searchSourcePurchases.map(purchase => ({
      ...purchase,
      searchText: [
        purchase['Dealer Name'] || '',
        purchase['Item Name'] || '',
        purchase['Date'] || '',
        purchase['Unit'] || '',
        purchase['Notes'] || '',
        String(purchase['Quantity'] || ''),
        String(purchase['Purchase Price/Unit'] || ''),
        String(purchase['Selling Price/Unit'] || ''),
        String(purchase['Amount'] || '')
      ].join(' ').toLowerCase()
    }));
  }, [searchSourcePurchases, debouncedSearchTerm]);

  // Memoized filtered purchases - optimized with pre-computed search text
  const filteredPurchases = useMemo(() => {
    if (!debouncedSearchTerm.trim()) {
      return [];
    }

    const searchLower = debouncedSearchTerm.toLowerCase();
    return searchablePurchases.filter(purchase =>
      purchase.searchText.includes(searchLower)
    );
  }, [searchablePurchases, debouncedSearchTerm]);

  const isSearchActive = debouncedSearchTerm.trim().length > 0;

  // Get unique item names for autocomplete (from inventory, which has unique/combined items)
  const uniqueItemNames = useMemo(() => {
    return [...new Set(
      inventory
        .map(item => {
          // Handle inventory item name - could be item_name or Item Name
          return item.item_name || item['Item Name'] || item.name || '';
        })
        .filter(name => name && name.trim())
        .sort()
    )];
  }, [inventory]);

  const handleSearchChange = useCallback((event) => {
    setSearchTerm(event.target.value);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  const handleRowsPerPageChange = useCallback(async (e) => {
    const newRowsPerPage = parseInt(e.target.value);
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);

    if (!debouncedSearchTerm.trim()) {
      try {
        setLoading(true);
        setError(null);

        const purchasesResponse = await backendApi.getPurchases(1, newRowsPerPage);

        const purchasesData = purchasesResponse.purchases || [];
        const totalPurchases = purchasesResponse.totalPurchases || 0;
        const transformedPurchases = purchasesData.map(transformPurchaseRecord);

        setAllPurchases(transformedPurchases);
        setTotalPurchasesCount(totalPurchases);
      } catch (err) {
        if (isExpectedEmptyDataScenario(err)) {
          setAllPurchases([]);
          setTotalPurchasesCount(0);
          setError(null);
          setInfoMessage(NEW_USER_PURCHASE_MESSAGE);
        } else {
          setError('Failed to load purchases data with new page size. Please try again.');
          console.error('Error loading purchases data:', err);
        }
      } finally {
        setLoading(false);
      }
    }
  }, [debouncedSearchTerm]);

  const renderPurchaseRowMemo = useCallback(
    (purchase, index) => renderPurchaseRow(purchase, index, handleOpenDialog, handleOpenDeleteDialog, navigate),
    [handleOpenDeleteDialog, handleOpenDialog, navigate]
  );

  // Conditionally handle pagination based on search mode
  const currentPagePurchases = useMemo(() => {
    // For search mode (client-side pagination), slice the filtered results
    if (isSearchActive) {
      const startIndex = (currentPage - 1) * rowsPerPage;
      return filteredPurchases.slice(startIndex, startIndex + rowsPerPage);
    }

    // For server-side pagination (non-search), use loaded page data directly
    // allPurchases already contains the correct page data from backend
    return allPurchases;
  }, [filteredPurchases, allPurchases, currentPage, rowsPerPage, isSearchActive]);

  const displayTotal = isSearchActive ? filteredPurchases.length : totalPurchasesCount;
  const displayFrom = displayTotal === 0 ? 0 : ((currentPage - 1) * rowsPerPage) + 1;
  const displayTo = displayTotal === 0 ? 0 : Math.min(currentPage * rowsPerPage, displayTotal);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>

      {/* Page Header (NOT scrollable) */}
      <Box sx={{ flexShrink: 0 }}>
      {/* Header section with title and total purchase card side by side */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        {/* Left side - Title, description, search, and button */}
        <Box sx={{ flex: 1, mr: 3, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h4" gutterBottom>
            Purchase Management
          </Typography>

          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Track and manage all your purchase transactions.
          </Typography>

          {/* Search Bar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <TextField
              placeholder="Search purchases by dealer, item, date, quantity, or price..."
              value={searchTerm}
              onChange={handleSearchChange}
              variant="outlined"
              size="small"
              sx={{ flex: 1, maxWidth: 500 }}
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
                ),
                endAdornment: (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {loadingFullData && (
                      <CircularProgress size={16} sx={{ color: 'primary.main' }} />
                    )}
                    {searchTerm && (
                      <IconButton size="small" onClick={clearSearch}>
                        <ClearIcon />
                      </IconButton>
                    )}
                  </Box>
                ),
              }}
            />
            {searchTerm && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Found {filteredPurchases.length} of {allPurchases.length} purchases
                </Typography>
                {loadingFullData && (
                  <Typography variant="caption" color="primary.main" sx={{ fontStyle: 'italic' }}>
                    Loading full data...
                  </Typography>
                )}
              </Box>
            )}

          </Box>

          {/* Add Purchase Button - positioned at bottom right */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              disabled={saving}
              size="small"
            >
              Add Purchase
            </Button>
          </Box>
        </Box>

        {/* Right side - Total Purchase Card */}
        <Box sx={{ flexShrink: 0, width: 280 }}>
          <Card sx={{
            background: 'linear-gradient(135deg, #059669 0%, #0d9488 100%)',
            color: 'white',
            borderRadius: 3,
            boxShadow: '0 8px 25px rgba(5, 150, 105, 0.3)',
            height: 'fit-content'
          }}>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                Total Purchase Value
              </Typography>
              <TotalPurchaseValue
                totalValue={totalPurchasesValue}
                totalCount={totalPurchasesCount}
                searchTerm={searchTerm}
                filteredCount={filteredPurchases.length}
              />
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                From all purchase transactions
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {(error || success || infoMessage) && (
        <Box sx={{ mb: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {infoMessage && !error && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {infoMessage}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}
        </Box>
      )}
      </Box>
      {/* Table Container (flex: 1; overflow: hidden) */}
      <Box sx={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
        <DataTable
          columns={PURCHASES_COLUMNS}
          data={currentPagePurchases}
          renderRow={renderPurchaseRowMemo}
          onEdit={handleOpenDialog}
          minWidth="800px"
          loading={loading}
          emptyMessage={searchTerm ? 'No purchases found matching your search.' : 'No purchases yet. Add your first purchase to get started.'}
        />
      </Box>

      {/* Pagination Footer (fixed height, not scrollable) */}
      <Box sx={{ flexShrink: 0, pt: 2 }}>
        {/* Rows per page selector below table */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Rows per page:
          </Typography>
          <FormControl size="small" sx={{ minWidth: 70 }}>
            <Select
              value={rowsPerPage}
              onChange={handleRowsPerPageChange}
              sx={{ fontSize: '0.875rem' }}
            >
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Typography variant="body2" color="text.secondary">
          Showing {displayFrom}-{displayTo} of {displayTotal} purchases
          {isSearchActive ? ` (filtered from ${totalPurchasesCount} total)` : ''}
          {!fullDataLoaded && !loading && !loadingFullData && ` • ${allPurchases.length} loaded for fast display`}
          {loadingFullData && ' • Loading full dataset...'}
        </Typography>

        <Pagination
          count={Math.max(1, Math.ceil(displayTotal / rowsPerPage))}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
          sx={{ display: 'flex', justifyContent: 'flex-end' }}
        />
      </Box>

      </Box>
      {/* Add/Edit Purchase Dialog */}
      <Dialog open={open} onClose={saving ? undefined : handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingPurchase ? 'Edit Purchase' : 'Add New Purchase'}
          {saving && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Processing...
              </Typography>
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Date"
            type="date"
            fullWidth
            variant="outlined"
            value={formData.date}
            onChange={(e) => handleInputChange('date', e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={saving}
          />

          <TextField
            margin="dense"
            label="Dealer Name"
            select
            fullWidth
            variant="outlined"
            value={formData.dealerName}
            onChange={(e) => handleInputChange('dealerName', e.target.value)}
            disabled={saving}
          >
            {dealers.map((dealer, index) => (
              <MenuItem key={dealer.id || index} value={dealer.name || dealer['Dealer Name'] || ''}>
                {dealer.name || dealer['Dealer Name'] || 'Unknown Dealer'}
              </MenuItem>
            ))}
          </TextField>

          <Autocomplete
            freeSolo
            options={uniqueItemNames}
            value={formData.itemName}
            onChange={(event, newValue) => {
              handleInputChange('itemName', newValue || '');
            }}
            onInputChange={(event, newInputValue) => {
              handleInputChange('itemName', newInputValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="dense"
                label="Item Name"
                fullWidth
                variant="outlined"
                disabled={saving}
                placeholder="Type or select existing item..."
              />
            )}
            disabled={saving}
          />

          <Box display="flex" gap={2}>
            <TextField
              margin="dense"
              label="Quantity"
              type="number"
              variant="outlined"
              value={formData.quantity}
              onChange={(e) => handleInputChange('quantity', e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
              sx={{ flex: 1 }}
              disabled={saving}
            />

            <TextField
              margin="dense"
              label="Unit"
              select
              variant="outlined"
              value={formData.unit}
              onChange={(e) => handleInputChange('unit', e.target.value)}
              sx={{ flex: 1 }}
              disabled={saving}
            >
              <MenuItem value="PCS">PCS</MenuItem>
              <MenuItem value="KG">KG</MenuItem>
              <MenuItem value="LTR">LTR</MenuItem>
              <MenuItem value="MTR">MTR</MenuItem>
            </TextField>
          </Box>

          <Box display="flex" gap={2}>
            <TextField
              margin="dense"
              label="Purchase Price per Unit"
              type="number"
              variant="outlined"
              value={formData.purchasePrice}
              onChange={(e) => handleInputChange('purchasePrice', e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
              sx={{ flex: 1 }}
              disabled={saving}
            />

            <TextField
              margin="dense"
              label="Selling Price per Unit"
              type="number"
              variant="outlined"
              value={formData.sellingPrice}
              onChange={(e) => handleInputChange('sellingPrice', e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
              sx={{ flex: 1 }}
              disabled={saving}
            />
          </Box>

          <TextField
            margin="dense"
            label="Notes (Optional)"
            multiline
            rows={2}
            variant="outlined"
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            disabled={saving}
            placeholder="Add any additional notes..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={saving || !formData.dealerName.trim() || !formData.itemName.trim() || formData.quantity <= 0}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? 'Processing...' : (editingPurchase ? 'Update Purchase' : 'Add Purchase')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Purchase Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Purchase</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Are you sure you want to delete this purchase?
          </Typography>
          {deletingPurchase && (
            <Typography variant="body2" color="text.secondary">
              Dealer: {deletingPurchase['Dealer Name'] || 'N/A'} | Item: {deletingPurchase['Item Name'] || 'N/A'}
            </Typography>
          )}
          <Typography variant="body2" color="error.main" sx={{ mt: 2 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : null}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Purchases;
